/**
 * HistoryTab - Audit trail for an annotation (21 CFR Part 11 compliant).
 *
 * Shows all changes made to the issue including:
 * - Status changes with reasons
 * - Field updates
 * - Comments added/deleted
 * - Attachments added/deleted
 * - Assignment changes
 */

import { format } from 'date-fns'
import {
  History,
  CheckCircle,
  Clock,
  MessageSquare,
  Paperclip,
  User,
  Edit,
  Plus,
  Trash2,
  Link2,
  Eye,
  Loader2,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { useHistory } from '@/hooks/useAnnotations'
import type { HistoryEntry } from '@/types/annotation'

interface HistoryTabProps {
  annotationId: number
  studyId: string
}

export function HistoryTab({ annotationId, studyId }: HistoryTabProps) {
  // Fetch history
  const { data, isLoading, error } = useHistory(studyId, annotationId)

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <Plus className="h-4 w-4 text-green-500" />
      case 'status_changed':
        return <CheckCircle className="h-4 w-4 text-blue-500" />
      case 'updated':
        return <Edit className="h-4 w-4 text-yellow-500" />
      case 'comment_added':
        return <MessageSquare className="h-4 w-4 text-purple-500" />
      case 'comment_deleted':
        return <MessageSquare className="h-4 w-4 text-red-500" />
      case 'attachment_added':
        return <Paperclip className="h-4 w-4 text-blue-500" />
      case 'attachment_deleted':
        return <Paperclip className="h-4 w-4 text-red-500" />
      case 'assigned':
        return <User className="h-4 w-4 text-cyan-500" />
      case 'link_created':
        return <Link2 className="h-4 w-4 text-indigo-500" />
      case 'link_deleted':
        return <Link2 className="h-4 w-4 text-red-500" />
      case 'viewed':
        return <Eye className="h-4 w-4 text-gray-500" />
      case 'deleted':
        return <Trash2 className="h-4 w-4 text-red-500" />
      default:
        return <Clock className="h-4 w-4 text-gray-500" />
    }
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created':
        return 'Created issue'
      case 'status_changed':
        return 'Changed status'
      case 'updated':
        return 'Updated issue'
      case 'comment_added':
        return 'Added comment'
      case 'comment_deleted':
        return 'Deleted comment'
      case 'attachment_added':
        return 'Added attachment'
      case 'attachment_deleted':
        return 'Deleted attachment'
      case 'assigned':
        return 'Changed assignment'
      case 'link_created':
        return 'Linked issue'
      case 'link_deleted':
        return 'Removed link'
      case 'viewed':
        return 'Viewed issue'
      case 'deleted':
        return 'Deleted issue'
      default:
        return action.replace(/_/g, ' ')
    }
  }

  const formatChanges = (entry: HistoryEntry) => {
    // If there's no field change info, nothing to show
    if (!entry.fieldName && !entry.oldValue && !entry.newValue) return null

    // Show field-level changes
    if (entry.fieldName) {
      // Status change
      if (entry.fieldName === 'status' && entry.oldValue && entry.newValue) {
        return (
          <div className="mt-1 text-sm">
            Status:{' '}
            <Badge variant="outline" className="mx-1">
              {entry.oldValue}
            </Badge>
            →
            <Badge variant="outline" className="ml-1">
              {entry.newValue}
            </Badge>
          </div>
        )
      }

      // Priority change
      if (entry.fieldName === 'priority' && entry.oldValue && entry.newValue) {
        return (
          <div className="mt-1 text-sm">
            Priority: {entry.oldValue} → {entry.newValue}
          </div>
        )
      }

      // Assignment change
      if (entry.fieldName === 'assigned_to_role' || entry.fieldName === 'assigned_to_user_id') {
        return (
          <div className="mt-1 text-sm">
            Assigned to: {entry.oldValue || 'Unassigned'} → {entry.newValue || 'Unassigned'}
          </div>
        )
      }

      // Generic field change
      return (
        <div className="mt-1 text-sm">
          {entry.fieldName}: {entry.oldValue || '(empty)'} → {entry.newValue || '(empty)'}
        </div>
      )
    }

    return null
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Failed to load history
      </div>
    )
  }

  const history = data?.history || []

  if (history.length === 0) {
    return (
      <div className="text-center py-8">
        <History className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
        <p className="text-muted-foreground">No history available</p>
      </div>
    )
  }

  return (
    <div className="space-y-1">
      {/* Compliance Notice */}
      <div className="text-xs text-muted-foreground bg-muted p-2 rounded mb-4">
        This audit trail is maintained for 21 CFR Part 11 compliance.
        All changes are permanently recorded.
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

        {history.map((entry) => (
          <div key={entry.id} className="relative pl-10 pb-6">
            {/* Timeline dot */}
            <div className="absolute left-2 top-1 w-5 h-5 rounded-full bg-background border-2 border-border flex items-center justify-center">
              {getActionIcon(entry.action)}
            </div>

            {/* Content */}
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">{getActionLabel(entry.action)}</p>
                  <p className="text-xs text-muted-foreground">
                    by {entry.changer?.fullName || 'System'} • {format(new Date(entry.createdAt), 'PPp')}
                  </p>
                </div>
              </div>

              {/* Change reason (for status changes) */}
              {entry.changeReason && (
                <div className="mt-2 p-2 bg-background rounded text-sm">
                  <span className="text-muted-foreground">Reason: </span>
                  {entry.changeReason}
                </div>
              )}

              {/* Changes details */}
              {formatChanges(entry)}

              {/* IP Address (for compliance) */}
              {entry.ipAddress && (
                <p className="text-xs text-muted-foreground mt-2">
                  IP: {entry.ipAddress}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
